/**
 * PublisherWrite — the writer's dashboard for creating and editing
 * canonical Publisher articles.
 *
 * Layout:
 *   • Top: title, summary, cover image URL, category / location metadata
 *   • Middle: language tabs (source + translations) → TipTap editor
 *   • Right rail: distribution list (site × location × category × policy)
 *   • Bottom: save draft / publish / preview
 *
 * Auth: requires a signed-in SocialPulse user (JWT under sp_token/token).
 * Access to another author's article returns 403 from the API.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Save, Send, ExternalLink, Loader2, PenLine, Globe, Languages,
  MapPin, Tag, Plus, X, ArrowLeft, Trash2, CheckCircle2,
} from "lucide-react";
import PublisherEditor from "@/components/publisher/PublisherEditor";
import { useTransliterate } from "@/hooks/useTransliterate";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
const CANONICAL_HOST = "https://blogs.socialpulse.com";

const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English",  dir: "ltr" },
  { code: "es", label: "Español",  dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "de", label: "Deutsch",  dir: "ltr" },
  { code: "pt", label: "Português",dir: "ltr" },
  { code: "hi", label: "हिन्दी",    dir: "ltr" },
  { code: "ml", label: "മലയാളം",   dir: "ltr" },
  { code: "ta", label: "தமிழ்",     dir: "ltr" },
  { code: "bn", label: "বাংলা",     dir: "ltr" },
  { code: "ar", label: "العربية",  dir: "rtl" },
  { code: "he", label: "עברית",    dir: "rtl" },
  { code: "zh", label: "中文",     dir: "ltr" },
  { code: "ja", label: "日本語",    dir: "ltr" },
];

const CATEGORY_OPTIONS = [
  "local", "world", "politics", "business", "technology", "health",
  "science", "climate", "sports", "entertainment", "travel", "lifestyle",
  "food", "agriculture", "education", "real-estate", "opinion", "guide",
  "community",
];

const EDITORIAL_LABELS = ["news", "opinion", "guide", "explainer", "event", "announcement", "review", "interview"];
const POLICIES = ["full_article", "excerpt", "headline_only"];
const SITE_OPTIONS = [
  "newspulse", "farmerspulse", "foodpulse", "homespulse", "airlinespulse",
  "worldpulse", "socialpulse",
];

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

async function api(path, opts = {}) {
  const token = getToken();
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`HTTP ${r.status}: ${detail}`);
  }
  return r.json();
}

export default function PublisherWrite() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const isNew = !articleId;
  const [token, setToken] = useState(getToken);
  const { user: authUser, loading: authLoading } = useAuth();
  // Re-check the stored token whenever the auth context hydrates so a
  // Firebase-restored session doesn't flicker through the sign-in gate.
  useEffect(() => {
    if (authUser) {
      const t = getToken();
      if (t) setToken(t);
    }
  }, [authUser]);
  const signedIn = Boolean(token) || Boolean(authUser);

  // Meta fields
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [primaryCategory, setPrimaryCategory] = useState("local");
  const [editorialLabel, setEditorialLabel] = useState("news");
  const [contentFormat, setContentFormat] = useState("article"); // article | note | podcast
  const [primaryLocationId, setPrimaryLocationId] = useState("");
  const [originalLanguage, setOriginalLanguage] = useState("en");

  // Editor content (source language + translations)
  const [bodyByLang, setBodyByLang] = useState({ en: "" });
  const [activeLang, setActiveLang] = useState("en");
  const [translationsMeta, setTranslationsMeta] = useState({}); // {lang: {title, summary}}
  const [translateBusy, setTranslateBusy] = useState(false);
  const [translateError, setTranslateError] = useState(null);

  // Live-transliteration hooks for the title + summary inputs so
  // writers stay in one language across the whole compose screen.
  // Auto-follows the active translation tab (ML tab → ML, EN tab → OFF).
  const titleRef = useRef(null);
  const summaryRef = useRef(null);
  const titleTranslit = useTransliterate({
    value: title,
    onChange: setTitle,
    inputRef: titleRef,
    preferredLang: activeLang,
    authToken: token,
    testIdPrefix: "publisher-write-title-translit",
  });
  const summaryTranslit = useTransliterate({
    value: summary,
    onChange: setSummary,
    inputRef: summaryRef,
    preferredLang: activeLang,
    authToken: token,
    testIdPrefix: "publisher-write-summary-translit",
  });

  // Loaded article state
  const [articleIdState, setArticleIdState] = useState(articleId || null);
  const [slug, setSlug] = useState(null);
  const [status, setStatus] = useState("draft");
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState(null);

  // Prefill from URL params when composing a new article — supports
  //   ?topic=<slug>            (from topic-card CTAs)
  //   ?title=&draft=&source=   (from "Turn answer into article")
  useEffect(() => {
    if (!isNew) return;
    const params = new URLSearchParams(loc.search);
    const pTitle = params.get("title");
    const pDraft = params.get("draft");
    const pTopic = params.get("topic");
    const pSource = params.get("source");
    if (pTitle) setTitle(pTitle);
    if (pTopic) setPrimaryCategory(pTopic);
    if (pDraft) {
      // Wrap raw text as a paragraph so TipTap renders it cleanly.
      const html = pDraft.includes("<")
        ? pDraft
        : pDraft.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, " ")}</p>`).join("");
      setBodyByLang((prev) => ({ ...prev, [originalLanguage]: html }));
    }
    if (pSource === "question" && pTitle && !summary) {
      setSummary(`Community answer expanded — first asked as: "${pTitle}"`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  const activeDir = useMemo(() => {
    const lang = SUPPORTED_LANGUAGES.find((l) => l.code === activeLang);
    return lang?.dir || "ltr";
  }, [activeLang]);

  const activeTranslationMeta = translationsMeta[activeLang] || null;

  // Load existing article + distributions + translations
  useEffect(() => {
    if (isNew || !articleId) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const doc = await api(`/articles/${articleId}`);
        if (!alive) return;
        setTitle(doc.title || "");
        setSummary(doc.summary || "");
        setCoverUrl(doc.cover_image_url || "");
        setPrimaryCategory(doc.primary_category || "local");
        setEditorialLabel(doc.editorial_label || "news");
        setContentFormat(doc.content_format || "article");
        setPrimaryLocationId(doc.primary_location_id || "");
        setOriginalLanguage(doc.original_language || "en");
        setSlug(doc.slug);
        setStatus(doc.status || "draft");
        setBodyByLang((prev) => ({
          ...prev,
          [doc.original_language || "en"]: doc.body_html || doc.body_markdown || "",
        }));
        setActiveLang(doc.original_language || "en");
        const [dists, trans] = await Promise.all([
          api(`/articles/${articleId}/distributions`),
          api(`/articles/${articleId}/translations`),
        ]);
        setDistributions(Array.isArray(dists) ? dists : []);
        const meta = {};
        for (const t of trans) meta[t.language] = { title: t.title, summary: t.summary, status: t.status };
        setTranslationsMeta(meta);
      } catch (e) {
        setError(e.message || "Could not load article");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [articleId, isNew]);

  const flash = () => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1600);
  };

  // Save draft (create on first save, patch afterwards)
  const saveDraft = useCallback(async () => {
    if (!title.trim() || !bodyByLang[originalLanguage]?.trim()) {
      setError("Title and body are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (!articleIdState) {
        const created = await api("/articles", {
          method: "POST",
          body: JSON.stringify({
            title, summary,
            body_html: bodyByLang[originalLanguage],
            original_language: originalLanguage,
            primary_location_id: primaryLocationId || null,
            primary_category: primaryCategory,
            editorial_label: editorialLabel,
            content_format: contentFormat,
            cover_image_url: coverUrl || null,
            status: "draft",
          }),
        });
        setArticleIdState(created.id);
        setSlug(created.slug);
        navigate(`/publisher/write/${created.id}`, { replace: true });
      } else {
        await api(`/articles/${articleIdState}`, {
          method: "PATCH",
          body: JSON.stringify({
            title, summary,
            body_html: bodyByLang[originalLanguage],
            primary_location_id: primaryLocationId || null,
            primary_category: primaryCategory,
            editorial_label: editorialLabel,
            content_format: contentFormat,
            cover_image_url: coverUrl || null,
          }),
        });
      }
      // Persist any translations that have body content
      for (const [lang, body] of Object.entries(bodyByLang)) {
        if (lang === originalLanguage) continue;
        if (!body?.trim()) continue;
        const meta = translationsMeta[lang] || {};
        const payload = {
          title: meta.title || title,
          summary: meta.summary || summary,
          body_html: body,
        };
        try {
          if (translationsMeta[lang]) {
            await api(`/articles/${articleIdState || ""}/translations/${lang}`, {
              method: "PATCH", body: JSON.stringify(payload),
            });
          } else {
            await api(`/articles/${articleIdState || ""}/translations`, {
              method: "POST", body: JSON.stringify({ language: lang, ...payload }),
            });
            setTranslationsMeta((m) => ({ ...m, [lang]: { ...payload, status: "human_reviewed" } }));
          }
        } catch (e) {
          console.warn(`translation save failed for ${lang}`, e);
        }
      }
      flash();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [
    title, summary, bodyByLang, originalLanguage, primaryLocationId,
    primaryCategory, editorialLabel, contentFormat, coverUrl, articleIdState, translationsMeta, navigate,
  ]);

  const publish = useCallback(async () => {
    if (!articleIdState) {
      await saveDraft();
    }
    setSaving(true);
    try {
      await api(`/articles/${articleIdState || ""}/publish`, { method: "POST" });
      setStatus("published");
      flash();
    } catch (e) {
      setError(e.message || "Publish failed");
    } finally {
      setSaving(false);
    }
  }, [articleIdState, saveDraft]);

  const addDistribution = useCallback(async (d) => {
    if (!articleIdState) {
      setError("Save the draft first before distributing.");
      return;
    }
    try {
      const r = await api(`/articles/${articleIdState}/distribute`, {
        method: "POST", body: JSON.stringify(d),
      });
      if (r.created) setDistributions((prev) => [...prev, { id: r.id, ...d, article_id: articleIdState }]);
    } catch (e) {
      setError(e.message || "Distribution failed");
    }
  }, [articleIdState]);

  const removeDistribution = useCallback(async (distId) => {
    if (!articleIdState) return;
    try {
      await api(`/articles/${articleIdState}/distributions/${distId}`, { method: "DELETE" });
      setDistributions((prev) => prev.filter((d) => d.id !== distId));
    } catch (e) {
      setError(e.message || "Remove failed");
    }
  }, [articleIdState]);

  const addLanguage = useCallback((lang) => {
    if (bodyByLang[lang] !== undefined) {
      setActiveLang(lang);
      return;
    }
    setBodyByLang((prev) => ({ ...prev, [lang]: "" }));
    setActiveLang(lang);
  }, [bodyByLang]);

  const activeBody = bodyByLang[activeLang] ?? "";

  if (!signedIn) {
    // While Firebase is still restoring the session on first paint,
    // don't bounce yet — otherwise a legitimately-signed-in user who
    // deep-links here mid-hydration gets kicked to /auth and back.
    if (authLoading) {
      return (
        <div className="min-h-screen grid place-items-center" style={{ background: "#FAFAF7" }} data-testid="publisher-write-auth-loading">
          <div className="text-slate-500 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your session…
          </div>
        </div>
      );
    }
    // Redirect straight to the beautiful blog-styled auth page —
    // never expose the fat SocialPulse chrome to a writer just
    // trying to publish. `?next=` returns them here after sign-in.
    const nextParam = encodeURIComponent(
      loc.pathname + (loc.search || ""),
    );
    return <Navigate to={`/auth?next=${nextParam}`} replace />;
  }

  if (loading) {
    return (
      <div className="py-24 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading your draft…
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }} data-testid="publisher-write-page">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-6">
      {/* Header */}
      <header className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: "rgba(15,118,110,0.12)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#0F766E" }}>
              SocialPulse<sup className="text-[8px]">®</sup> Blog · Compose
            </div>
            <h1 className="text-xl truncate" style={{ fontFamily: "'Lora', serif", fontWeight: 600, color: "#0F172A" }}>
              {isNew ? "New article" : slug ? slug : "Editing article"}
            </h1>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ml-2 ${
            status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
          }`}
            data-testid="publisher-write-status">
            {status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash && (
            <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid="publisher-write-saved">
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {slug && (
            <a
              href={`${CANONICAL_HOST}/article/${slug}`}
              target="_blank" rel="noopener"
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="publisher-write-preview"
            >
              <ExternalLink className="w-3 h-3" /> Preview
            </a>
          )}
          <button
            type="button" onClick={saveDraft} disabled={saving}
            className="rounded-full bg-white border hover:border-slate-500 px-4 py-1.5 text-sm font-semibold text-slate-800 disabled:opacity-40"
            style={{ borderColor: "rgba(15,23,42,0.15)" }}
            data-testid="publisher-write-save"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button" onClick={publish} disabled={saving || status === "published"}
            className="inline-flex items-center gap-1 rounded-full text-white px-4 py-1.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: status === "published" ? "#94A3B8" : "#0F766E" }}
            data-testid="publisher-write-publish"
          >
            <Send className="w-3.5 h-3.5" /> {status === "published" ? "Published" : "Publish"}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* MAIN */}
        <div className="min-w-0 space-y-4">
          {/* Metadata card */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            {/* Format toggle: full Article vs short-form Note */}
            <div
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1 text-xs font-semibold"
              data-testid="publisher-write-format-toggle"
              role="tablist"
              aria-label="Content format"
            >
              <button
                type="button"
                onClick={() => setContentFormat("article")}
                className={`rounded-full px-3 py-1 transition ${
                  contentFormat === "article" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
                data-testid="publisher-write-format-article"
                role="tab"
                aria-selected={contentFormat === "article"}
              >
                Article
              </button>
              <button
                type="button"
                onClick={() => setContentFormat("note")}
                className={`rounded-full px-3 py-1 transition ${
                  contentFormat === "note" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
                data-testid="publisher-write-format-note"
                role="tab"
                aria-selected={contentFormat === "note"}
                title="Short-form update — renders inline in widgets, no click-out needed"
              >
                Note
              </button>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400 font-normal">
                {contentFormat === "note" ? "Short-form · renders inline in widgets" : "Long-form"}
              </span>
            </div>
            <div className="relative">
              <input
                ref={titleRef}
                type="text" value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={titleTranslit.onKeyDown}
                placeholder={contentFormat === "note" ? "Note headline" : "Article title"}
                className="w-full text-2xl sm:text-3xl font-bold outline-none placeholder:text-slate-300 pr-20"
                data-testid="publisher-write-title"
                maxLength={200}
              />
              <div className="absolute top-2 right-0">
                {titleTranslit.chip}
              </div>
            </div>
            <div className="relative">
              <textarea
                ref={summaryRef}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                onKeyDown={summaryTranslit.onKeyDown}
                placeholder={contentFormat === "note"
                  ? "Optional one-line context (skip and let the body speak)"
                  : "One-sentence summary (shown on widget cards and social previews)"}
                className="w-full text-sm text-slate-700 outline-none placeholder:text-slate-400 resize-none pr-20"
                rows={2} maxLength={500}
                data-testid="publisher-write-summary"
              />
              <div className="absolute top-1 right-0">
                {summaryTranslit.chip}
              </div>
            </div>
          </section>

          {/* Language tabs + editor */}
          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 flex items-center gap-1 px-3 py-2 overflow-x-auto" data-testid="publisher-write-lang-tabs">
              <Languages className="w-4 h-4 text-slate-400 shrink-0" />
              {Object.keys(bodyByLang).map((lang) => {
                const meta = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
                const isSource = lang === originalLanguage;
                return (
                  <button
                    key={lang} type="button"
                    onClick={() => setActiveLang(lang)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      activeLang === lang
                        ? "bg-blue-600 text-white"
                        : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`publisher-write-lang-${lang}`}
                  >
                    {meta?.label || lang}
                    {isSource && <span className="text-[9px] uppercase opacity-70">source</span>}
                  </button>
                );
              })}
              <select
                onChange={(e) => e.target.value && addLanguage(e.target.value)}
                value=""
                className="text-xs border border-slate-200 rounded-full px-3 py-1 bg-white hover:border-slate-400 outline-none"
                data-testid="publisher-write-add-lang"
              >
                <option value="" disabled>+ Add translation</option>
                {SUPPORTED_LANGUAGES
                  .filter((l) => bodyByLang[l.code] === undefined)
                  .map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>

            {activeLang !== originalLanguage && (
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex flex-wrap items-center gap-3" data-testid="publisher-write-lang-hint">
                <span className="text-xs text-amber-800 flex-1 min-w-[220px]">
                  Editing the <strong>{SUPPORTED_LANGUAGES.find((l) => l.code === activeLang)?.label}</strong> translation.
                  The source ({SUPPORTED_LANGUAGES.find((l) => l.code === originalLanguage)?.label}) is unchanged.
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    if (translateBusy) return;
                    const sourceHtml = bodyByLang[originalLanguage] || "";
                    if (!sourceHtml.trim()) {
                      setTranslateError("Write something in the source language first.");
                      return;
                    }
                    if (activeBody.trim() &&
                        !window.confirm("This will overwrite the current translation. Continue?")) {
                      return;
                    }
                    setTranslateBusy(true);
                    setTranslateError(null);
                    try {
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1/translate`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          source_html: sourceHtml,
                          source_lang: originalLanguage,
                          target_lang: activeLang,
                        }),
                      });
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                      const d = await r.json();
                      setBodyByLang((prev) => ({ ...prev, [activeLang]: d.target_html || "" }));
                    } catch (e) {
                      setTranslateError(e.message || "Translation failed. Please try again.");
                    } finally {
                      setTranslateBusy(false);
                    }
                  }}
                  disabled={translateBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-semibold disabled:opacity-40"
                  style={{ background: "#0F766E" }}
                  data-testid="publisher-write-translate-from-source"
                >
                  {translateBusy ? "Translating…" : "Translate from source ›"}
                </button>
                {translateError && (
                  <span className="text-xs text-red-700" data-testid="publisher-write-translate-error">
                    {translateError}
                  </span>
                )}
              </div>
            )}

            <div dir={activeDir}>
              <PublisherEditor
                key={activeLang} // remount when language changes
                value={activeBody}
                onChange={(html) => setBodyByLang((prev) => ({ ...prev, [activeLang]: html }))}
                placeholder="Start writing… drop in images, YouTube links, or paste any URL and use the ✨ button to unfurl it."
                authToken={token}
                testIdPrefix={`publisher-write-editor-${activeLang}`}
                preferredTranslitLang={activeLang}
              />
            </div>
          </section>
        </div>

        {/* RIGHT RAIL */}
        <aside className="space-y-4">
          {/* Cover image */}
          <section className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Cover image</div>
            <input type="url" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://…"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-400"
              data-testid="publisher-write-cover-url" />
            {coverUrl && (
              <img src={coverUrl} alt="Cover preview"
                className="mt-2 w-full aspect-video object-cover rounded-lg border border-slate-100" />
            )}
          </section>

          {/* Taxonomy */}
          <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-1">
                <Tag className="w-3 h-3" /> Primary category
              </label>
              <select value={primaryCategory} onChange={(e) => setPrimaryCategory(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-blue-400"
                data-testid="publisher-write-primary-category">
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                Editorial label
              </label>
              <select value={editorialLabel} onChange={(e) => setEditorialLabel(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-blue-400"
                data-testid="publisher-write-editorial-label">
                {EDITORIAL_LABELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-1">
                <MapPin className="w-3 h-3" /> Primary location
              </label>
              <input type="text" value={primaryLocationId}
                onChange={(e) => setPrimaryLocationId(e.target.value)}
                placeholder="IN-KL-kochi   (country-admin1-city)"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-400 font-mono"
                data-testid="publisher-write-primary-location" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-1">
                <Globe className="w-3 h-3" /> Source language
              </label>
              <select value={originalLanguage}
                onChange={(e) => setOriginalLanguage(e.target.value)}
                disabled={!isNew}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none disabled:bg-slate-100"
                data-testid="publisher-write-original-language">
                {SUPPORTED_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              {!isNew && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Source language is locked once the article exists.
                </div>
              )}
            </div>
          </section>

          {/* Distributions */}
          <section className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="publisher-write-distributions">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              Distribution ({distributions.length})
            </div>
            <ul className="space-y-1.5 mb-3">
              {distributions.map((d) => (
                <li key={d.id}
                  className="flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-1.5 text-xs"
                  data-testid={`publisher-dist-${d.id}`}>
                  <span className="font-semibold text-slate-900">{d.site_id}</span>
                  {d.location_id && <span className="font-mono text-slate-500">· {d.location_id}</span>}
                  {d.category_id && <span className="text-slate-500">· {d.category_id}</span>}
                  <span className="ml-auto rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[10px] uppercase text-slate-600">
                    {d.policy}
                  </span>
                  <button type="button"
                    onClick={() => removeDistribution(d.id)}
                    aria-label="Remove distribution"
                    className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
              {distributions.length === 0 && (
                <li className="text-xs text-slate-500 italic">
                  Not distributed anywhere yet. Add rows below.
                </li>
              )}
            </ul>
            <AddDistributionForm onAdd={addDistribution} />
          </section>
        </aside>
      </div>
      </div>
    </div>
  );
}


function AddDistributionForm({ onAdd }) {
  const [siteId, setSiteId] = useState("newspulse");
  const [locationId, setLocationId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [policy, setPolicy] = useState("excerpt");
  const submit = (e) => {
    e.preventDefault();
    onAdd({
      site_id: siteId,
      location_id: locationId.trim() || null,
      category_id: categoryId.trim() || null,
      policy,
      status: "allowed",
    });
    setLocationId(""); setCategoryId("");
  };
  return (
    <form onSubmit={submit} className="space-y-2 border-t border-slate-100 pt-3" data-testid="publisher-add-dist-form">
      <div className="grid grid-cols-2 gap-2">
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
          data-testid="publisher-add-dist-site">
          {SITE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={policy} onChange={(e) => setPolicy(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
          data-testid="publisher-add-dist-policy">
          {POLICIES.map((p) => <option key={p} value={p}>{p.replace("_", " ")}</option>)}
        </select>
      </div>
      <input type="text" value={locationId} onChange={(e) => setLocationId(e.target.value)}
        placeholder="Location (optional, e.g. IN-KL)"
        className="w-full text-xs border border-slate-200 rounded-md px-2 py-1 font-mono"
        data-testid="publisher-add-dist-location" />
      <input type="text" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
        placeholder="Category (optional, e.g. agriculture)"
        className="w-full text-xs border border-slate-200 rounded-md px-2 py-1"
        data-testid="publisher-add-dist-category" />
      <button type="submit"
        className="w-full inline-flex items-center justify-center gap-1 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-1.5"
        data-testid="publisher-add-dist-submit">
        <Plus className="w-3 h-3" /> Add
      </button>
    </form>
  );
}
